import { useId, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { mensajeDeCampo } from '../api';
import type { ModificarUsuarioRequest, Rol, Usuario } from '../api';
import {
  activarUsuario,
  cambiarPassword,
  cambiosDe,
  desactivarUsuario,
  modificarUsuario,
} from './api';
import { useRefrescarUsuarios } from './consultas';
import { mensajeDeUsuarios } from './mensajes';
import type { Operacion } from './mensajes';
import { motivoParaNoCambiarRol, motivoParaNoDesactivar } from './reglas';

/**
 * Una fila del ABM de usuarios: los datos, y las cuatro cosas que se le pueden
 * hacer a una persona (KAN-143).
 *
 * # POR QUÉ CADA FILA MANEJA SU PROPIO ERROR
 *
 * Un `409` sobre la fila de Juana no tiene nada que decirle a quien está mirando
 * la de Pedro. Un cartel único arriba de la tabla obligaría a leerlo y después
 * buscar a qué fila se refería; peor, con dos filas editadas seguidas mostraría
 * el error de la anterior. La mutación vive acá, así que el error aparece
 * exactamente donde se apretó el botón.
 *
 * # UNA SOLA MUTACIÓN PARA LAS CUATRO ACCIONES
 *
 * Cuatro `useMutation` darían cuatro estados de error simultáneos en la misma
 * fila. Con una sola, disparar una acción **borra el error de la anterior**, que
 * es lo que la persona espera: reintentó, y lo viejo dejó de valer. Qué operación
 * falló se sabe por `variables`, sin leer el `detalle` de la respuesta.
 *
 * # LOS NOMBRES ACCESIBLES LLEVAN EL EMAIL
 *
 * Hay un "Editar" por fila. El texto visible dice solo "Editar" —la fila ya dice
 * de quién—, pero el nombre accesible lleva el email: quien navegue por teclado o
 * con un lector de pantalla escucha "Editar a juana@ejemplo.com" y no diez
 * botones idénticos. El email y no el nombre porque es único y no cambia mientras
 * se lo está editando.
 */
export function FilaDeUsuario({ usuario, yoId }: { usuario: Usuario; yoId: number }) {
  const idNombre = useId();
  const idRol = useId();
  const idPassword = useId();

  const [modo, setModo] = useState<'ver' | 'editar' | 'password'>('ver');
  const [nombre, setNombre] = useState(usuario.nombre);
  const [rol, setRol] = useState<Rol>(usuario.rol);
  const [password, setPassword] = useState('');

  const refrescar = useRefrescarUsuarios();

  const accion = useMutation({
    mutationFn: (pedido: Pedido) => ejecutar(usuario.id, pedido),
    onSuccess: () => {
      setModo('ver');
      setPassword('');
      refrescar();
    },
  });

  const noDesactivar = motivoParaNoDesactivar(usuario, yoId);
  const noCambiarRol = motivoParaNoCambiarRol(usuario, yoId);
  const errorDeNombre = mensajeDeCampo(accion.error, 'nombre');
  const errorDePassword = mensajeDeCampo(accion.error, 'password');
  const quien = usuario.email;

  function abrirEdicion(): void {
    setNombre(usuario.nombre);
    setRol(usuario.rol);
    accion.reset();
    setModo('editar');
  }

  function abrirPassword(): void {
    setPassword('');
    accion.reset();
    setModo('password');
  }

  function guardarEdicion(): void {
    // PATCH PARCIAL: viaja solo lo que se tocó. Ver `cambiosDe`.
    const cambios = cambiosDe(usuario, { nombre, rol });
    if (!cambios) {
      setModo('ver'); // Nada cambió: no se gasta un pedido.
      return;
    }
    accion.mutate({ tipo: 'modificar', cambios });
  }

  return (
    <>
      <tr className={usuario.activo ? undefined : 'inactivo'}>
        <td>
          {modo === 'editar' ? (
            <>
              <label htmlFor={idNombre} className="oculto-visualmente">
                Nombre de {quien}
              </label>
              <input
                id={idNombre}
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                aria-invalid={errorDeNombre !== null}
                aria-describedby={errorDeNombre ? `${idNombre}-error` : undefined}
              />
              {errorDeNombre ? (
                <span id={`${idNombre}-error`} className="error">
                  {' '}
                  {errorDeNombre}
                </span>
              ) : null}
            </>
          ) : (
            usuario.nombre
          )}
        </td>
        <td>
          {quien}
          {usuario.id === yoId ? <span className="nota"> (sos vos)</span> : null}
        </td>
        <td>
          {modo === 'editar' ? (
            <>
              <label htmlFor={idRol} className="oculto-visualmente">
                Rol de {quien}
              </label>
              <select
                id={idRol}
                value={rol}
                onChange={(e) => setRol(e.target.value as Rol)}
                // REGLA 2 sobre la fila propia. El control se bloquea entero y no
                // solo la opción OPERADOR: esta pantalla solo la ve un ADMIN, así
                // que cualquier cambio de rol acá sería una degradación.
                disabled={noCambiarRol !== null}
                aria-describedby={noCambiarRol ? `${idRol}-motivo` : undefined}
              >
                <option value="OPERADOR">OPERADOR</option>
                <option value="ADMIN">ADMIN</option>
              </select>
              {noCambiarRol ? (
                <span id={`${idRol}-motivo`} className="nota">
                  {' '}
                  {noCambiarRol}
                </span>
              ) : null}
            </>
          ) : (
            usuario.rol
          )}
        </td>
        <td>{usuario.activo ? 'Activo' : 'Inactivo'}</td>
        <td>
          {modo === 'ver' ? (
            <Acciones
              usuario={usuario}
              noDesactivar={noDesactivar}
              idMotivo={`${idNombre}-motivo-baja`}
              ocupada={accion.isPending}
              alEditar={abrirEdicion}
              alCambiarPassword={abrirPassword}
              alDesactivar={() => accion.mutate({ tipo: 'desactivar' })}
              alActivar={() => accion.mutate({ tipo: 'activar' })}
            />
          ) : null}

          {modo === 'editar' ? (
            <>
              <button
                type="button"
                aria-label={`Guardar los cambios de ${quien}`}
                disabled={accion.isPending}
                onClick={guardarEdicion}
              >
                Guardar
              </button>{' '}
              <button
                type="button"
                aria-label={`Cancelar la edición de ${quien}`}
                onClick={() => setModo('ver')}
              >
                Cancelar
              </button>
            </>
          ) : null}

          {modo === 'password' ? (
            <>
              <label htmlFor={idPassword} className="oculto-visualmente">
                Contraseña nueva de {quien}
              </label>
              <input
                id={idPassword}
                type="password"
                // Es la contraseña de otra persona: que el navegador no la ofrezca
                // guardar como si fuera la de quien está usando la pantalla.
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-invalid={errorDePassword !== null}
                aria-describedby={errorDePassword ? `${idPassword}-error` : undefined}
              />
              {errorDePassword ? (
                <span id={`${idPassword}-error`} className="error">
                  {' '}
                  {errorDePassword}
                </span>
              ) : null}{' '}
              <button
                type="button"
                aria-label={`Guardar la contraseña nueva de ${quien}`}
                disabled={accion.isPending}
                onClick={() => accion.mutate({ tipo: 'password', password })}
              >
                Guardar contraseña
              </button>{' '}
              <button
                type="button"
                aria-label={`Cancelar el cambio de contraseña de ${quien}`}
                onClick={() => setModo('ver')}
              >
                Cancelar
              </button>
            </>
          ) : null}
        </td>
      </tr>

      {accion.isError ? (
        <tr className="fila-error">
          <td colSpan={5}>
            <span role="alert" className="error">
              {mensajeDeUsuarios(accion.error, operacionDe(accion.variables))}
            </span>
          </td>
        </tr>
      ) : null}
    </>
  );
}

/**
 * Los botones de la fila en reposo.
 *
 * **La baja NUNCA se llama "eliminar".** Es lógica: la fila queda para poder
 * atribuir un comprobante a quien lo pidió, y los inactivos siguen listados
 * justamente para que se los pueda reactivar. Ponerle "eliminar" a un botón que
 * no borra sería mentir sobre lo único que importa de esta pantalla.
 */
function Acciones({
  usuario,
  noDesactivar,
  idMotivo,
  ocupada,
  alEditar,
  alCambiarPassword,
  alDesactivar,
  alActivar,
}: {
  usuario: Usuario;
  /** Motivo por el que la baja está bloqueada, o `null`. Ver `reglas.ts`. */
  noDesactivar: string | null;
  idMotivo: string;
  ocupada: boolean;
  alEditar: () => void;
  alCambiarPassword: () => void;
  alDesactivar: () => void;
  alActivar: () => void;
}) {
  const quien = usuario.email;
  return (
    <>
      <button type="button" aria-label={`Editar a ${quien}`} onClick={alEditar}>
        Editar
      </button>{' '}
      <button
        type="button"
        aria-label={`Cambiar la contraseña de ${quien}`}
        onClick={alCambiarPassword}
      >
        Cambiar contraseña
      </button>{' '}
      {usuario.activo ? (
        <>
          <button
            type="button"
            aria-label={`Desactivar a ${quien}`}
            // REGLA 1 sobre la fila propia.
            disabled={noDesactivar !== null || ocupada}
            aria-describedby={noDesactivar ? idMotivo : undefined}
            onClick={alDesactivar}
          >
            Desactivar
          </button>
          {noDesactivar ? (
            // El motivo va como texto, no como `title`: un botón deshabilitado no
            // recibe foco ni hover en teclado, así que un tooltip sería invisible
            // justo para quien más lo necesita.
            <span id={idMotivo} className="nota">
              {' '}
              {noDesactivar}
            </span>
          ) : null}
        </>
      ) : (
        <button
          type="button"
          aria-label={`Reactivar a ${quien}`}
          disabled={ocupada}
          onClick={alActivar}
        >
          Reactivar
        </button>
      )}
    </>
  );
}

/** Lo que se le puede pedir al backend sobre una fila. */
type Pedido =
  | { tipo: 'modificar'; cambios: ModificarUsuarioRequest }
  | { tipo: 'password'; password: string }
  | { tipo: 'desactivar' }
  | { tipo: 'activar' };

function ejecutar(id: number, pedido: Pedido): Promise<Usuario> {
  switch (pedido.tipo) {
    case 'modificar':
      return modificarUsuario(id, pedido.cambios);
    case 'password':
      return cambiarPassword(id, pedido.password);
    case 'desactivar':
      return desactivarUsuario(id);
    case 'activar':
      return activarUsuario(id);
  }
}

/**
 * Qué operación falló. Sale de lo que se pidió, **no de mirar la respuesta**: el
 * backend usa un solo `operacion_invalida` para las dos reglas de la fila propia,
 * y distinguirlas por el `detalle` sería exactamente lo que no se hace acá.
 */
function operacionDe(pedido: Pedido | undefined): Operacion {
  return pedido?.tipo ?? 'modificar';
}
